import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw-src.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet/dist/leaflet.css";
import "../leaflet_icons/MarkerCluster.Default.css";

import indigo from "@material-ui/core/colors/indigo";
import Paper from "@material-ui/core/Paper";
import {
  StyledComponentProps,
  StyleRulesCallback,
  Theme,
  withStyles,
  withTheme,
} from "@material-ui/core/styles";
import * as geojson from "geojson";
import * as L from "leaflet";
import * as _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { CustomerGroupFilterState } from "src/types";

import { drawHints } from "../leaflet_settings";
import { CustomerGroupListItem } from "../lib/models/CustomerGroupListItem";
import { Dispatch, StoreState } from "../store";
import { isFeatureEnabled, numberFormat } from "../utilities";
import MapOverlayHintZoom from "./MapOverlayHintZoom";
import ProgressOverlay from "./ProgressOverlay";

function mapStateToProps({
  activeCustomerGroup,
  customerGroupFilter,
  latLong,
  polygonDensity,
  customerDensity,
}: StoreState) {
  return {
    activeCustomerGroup,
    customerDensity,
    customerGroupFilter,
    latLong,
    polygonDensity,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    changeCustomerGroupFilter: (payload: any) =>
      dispatch.customerGroupFilter.changeCustomerGroupFilter(payload),
    fetchCustomerDensity: (customerGroupFilter: CustomerGroupFilterState) =>
      dispatch.customerDensity.fetchCustomerDensity(customerGroupFilter),
    fetchLatLong: (customerGroupFilter: CustomerGroupFilterState) =>
      dispatch.latLong.fetchLatLong(customerGroupFilter),
    fetchPolygonDensity: (customerGroupFilter: CustomerGroupFilterState) =>
      dispatch.polygonDensity.fetchPolygonDensity(customerGroupFilter),
    setActiveCustomerGroup: (customerGroup: CustomerGroupListItem | null) =>
      dispatch.activeCustomerGroup.setActiveCustomerGroup(customerGroup),
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

interface State {
  density: number;
  postcode: string;
  selectionLayers: Array<L.Polygon | L.Rectangle>;
  showPressCtrlToZoomHint: boolean;
  isMounted: boolean;
}

class MapSelection extends React.Component<
  Props & { theme: Theme } & StyledComponentProps,
  State
> {
  public state = {
    density: 0,
    postcode: "",
    selectionLayers: [] as Array<L.Polygon | L.Rectangle>,
    showPressCtrlToZoomHint: false,
    isMounted: false,
  };

  private geojson: L.GeoJSON<any>;

  private map: L.Map;

  private markers: L.Marker[] = [];

  private legend = new L.Control({ position: "bottomright" });

  private singleHouseImg = require("../leaflet_icons/haus.png");

  private multiHouseImg = require("../leaflet_icons/mehrfamilienhaus.png");

  private multiHouseIcon = L.icon({
    iconSize: [40, 40],
    iconUrl: this.multiHouseImg,
  });

  private singleHouseIcon = L.icon({
    iconSize: [25, 30],
    iconUrl: this.singleHouseImg,
  });

  private clusterGroup = L.markerClusterGroup({
    maxClusterRadius: 40,
  });

  private fetchPolygonDensityDebounced = _.debounce((props: Props) => {
    props.fetchPolygonDensity(this.props.customerGroupFilter);
  }, 500);

  private fetchDataDebounced = _.debounce(
    (props: Props) =>
      props.fetchCustomerDensity(this.props.customerGroupFilter),
    500
  );

  private fetchLatLongDebounced = _.debounce(
    (props: Props) => props.fetchLatLong(this.props.customerGroupFilter),
    500
  );

  private enableMapOverlayHintZoom = _.throttle(() => {
    if (!this.ctrlKeyDown) {
      this.setState({ showPressCtrlToZoomHint: true });
    }
  }, 100);

  private disableMapOverlayHintZoomAfterTimeout = _.debounce(() => {
    this.setState({ showPressCtrlToZoomHint: false });
  }, 1000);

  private ctrlKeyDown = false;

  private drawnItems = new L.FeatureGroup();

  private drawControl = new L.Control.Draw({
    draw: {
      circle: false,
      circlemarker: false,
      marker: false,
      polyline: false,
    },
    edit: {
      featureGroup: this.drawnItems,
    },
  });

  private setDrawMenuHints = () => {
    L.drawLocal.draw = drawHints.draw;
    L.drawLocal.edit = drawHints.edit;
  };

  private setZoomHints = () => {
    const zoomInAnchorElement = document.querySelector<HTMLAnchorElement>(
      ".leaflet-control-zoom-in"
    );
    const zoomOutAnchorElement = document.querySelector<HTMLAnchorElement>(
      ".leaflet-control-zoom-out"
    );
    if (zoomInAnchorElement && zoomOutAnchorElement) {
      zoomInAnchorElement.title = "Karte vergrößern";
      zoomOutAnchorElement.title = "Karte verkleinern";
    }
  };

  public async componentDidMount() {
    this.setDrawMenuHints();
    this.setState((prev) => ({ ...prev, isMounted: true }));
    const map = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }
    );

    const options: L.MapOptions = {
      // Fallback center: Kiel.
      center: [
        parseFloat(process.env.REACT_APP_MAP_CENTER_LAT || "54.3418925"),
        parseFloat(process.env.REACT_APP_MAP_CENTER_LNG || "10.0554665"),
      ],
      layers: [map, this.clusterGroup],
      scrollWheelZoom: false,
      zoom: 10,
      zoomSnap: 0,
    };

    this.map = L.map("map", options);

    const overlayMarkers = {
      Marker: this.clusterGroup,
    };

    L.control.layers(undefined, overlayMarkers).addTo(this.map);

    this.map.addLayer(this.drawnItems);

    this.map.addControl(this.drawControl);

    this.drawPolygonsFromCustomerGroupFilter(this.drawnItems);

    this.map.on(L.Draw.Event.CREATED, (event: L.DrawEvents.Created) => {
      this.handleDrawCreated(
        event.layer as L.Polygon | L.Rectangle,
        this.map,
        this.drawnItems
      );
    });

    this.map.on(L.Draw.Event.EDITED, (event: L.DrawEvents.Edited) => {
      this.handleDrawEdited(
        event.layers as L.LayerGroup<L.Polygon | L.Rectangle>
      );
    });

    this.map.on(L.Draw.Event.DELETED, (event: L.DrawEvents.Deleted) => {
      this.handleDeleteDraw(
        event.layers as L.LayerGroup<L.Polygon | L.Rectangle>
      );
    });

    this.createMapLegend();
    this.fetchData();
    this.createClusters(this.clusterGroup);

    document.getElementById("map")!.focus();
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  public async componentWillUnmount() {
    this.setState((prev) => ({ ...prev, isMounted: false }));
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }

  public async componentDidUpdate(previousProps: Props, previousState: State) {
    if (this.state.isMounted) {
      this.setZoomHints();
      if (
        this.props.activeCustomerGroup &&
        !_.isEqual(
          previousProps.activeCustomerGroup,
          this.props.activeCustomerGroup
        )
      ) {
        // Unfortunately this is ultra hacky. Didn't find another solution though.
        this.map.removeLayer(this.drawnItems);
        this.map.removeControl(this.drawControl);
        this.drawnItems = new L.FeatureGroup();
        (this
          .drawControl as any)._toolbars.edit.options.featureGroup = this.drawnItems;
        this.map.addControl(this.drawControl);
        this.map.addLayer(this.drawnItems);
        this.drawPolygonsFromCustomerGroupFilter(this.drawnItems);
      }
      const customerGroupFilterChangedApartFromPolygonIds = () => {
        const previousCustomerGroupFilterWithPolygonIdsNeglected = {
          ...previousProps.customerGroupFilter,
          polygonMapSelection: previousProps.customerGroupFilter.polygonMapSelection.map(
            (selection) => selection.polygon
          ),
        };
        const newCustomerGroupFilterWithPolygonIdsNeglected = {
          ...this.props.customerGroupFilter,
          polygonMapSelection: this.props.customerGroupFilter.polygonMapSelection.map(
            (selection) => selection.polygon
          ),
        };
        return !_.isEqual(
          previousCustomerGroupFilterWithPolygonIdsNeglected,
          newCustomerGroupFilterWithPolygonIdsNeglected
        );
      };
      if (this.state.selectionLayers !== previousState.selectionLayers) {
        if (this.state.selectionLayers.length) {
          this.fitMapBoundsToPolygonSelections();
        } else {
          this.map.setZoom(10);
        }
      }
      if (customerGroupFilterChangedApartFromPolygonIds()) {
        if (
          _.isEqual(
            previousProps.activeCustomerGroup,
            this.props.activeCustomerGroup
          )
        ) {
          this.props.setActiveCustomerGroup(null);
        }
        if (this.state.selectionLayers.length) {
          this.fetchPolygonDensityDebounced(this.props);
        } else {
          this.fetchDataDebounced(this.props);
        }
        this.fetchLatLongDebounced(this.props);
      }

      if (
        previousProps.customerDensity !== this.props.customerDensity ||
        previousProps.customerGroupFilter.customerAddressPostalCodes !==
          this.props.customerGroupFilter.customerAddressPostalCodes ||
        previousProps.polygonDensity !== this.props.polygonDensity
      ) {
        const style = (feature: any) => {
          return {
            color: _.includes(
              this.props.customerGroupFilter.customerAddressPostalCodes.map(
                (item) => item.value
              ),
              feature.properties.postcode
            )
              ? this.props.theme.palette.secondary.main
              : this.props.theme.palette.primary.main,
            dashArray: "5",
            fillColor: this.state.selectionLayers.length
              ? "transparent"
              : this.getColor(
                  feature.properties.count.count,
                  feature.properties.density
                ),
            fillOpacity: 0.7,
            opacity: 1,
            weight: 3,
          };
        };

        if (this.geojson) {
          this.map.removeLayer(this.geojson);
        }
        this.geojson = L.geoJSON(this.props.customerDensity, {
          onEachFeature: this.onEachFeature,
          style,
        }).addTo(this.map);

        if (!this.state.selectionLayers.length) {
          this.createMapLegend();
        } else {
          this.addTooltipToPolygonSelections();
          this.removeMapLegend();
        }
      }

      if (previousProps.latLong !== this.props.latLong) {
        this.removeClusters(this.clusterGroup);
        this.createClusters(this.clusterGroup);
      }
      if (this.map.getZoom() === 18) {
        for (const i of this.markers) {
          if (this.clusterGroup.getVisibleParent(i) != null) {
            this.clusterGroup.getVisibleParent(i).setIcon(this.multiHouseIcon);
            i.setIcon(this.singleHouseIcon);
          }
        }
      }
      if (this.state.selectionLayers.length) {
        this.state.selectionLayers.forEach((selectionLayer) =>
          selectionLayer.bringToFront()
        );
      }
    }
  }

  public render() {
    return (
      <Paper>
        <ProgressOverlay
          loading={
            this.props.latLong.loading || this.props.customerDensity.loading
          }
        >
          <MapOverlayHintZoom
            showPressCtrlToZoomHint={this.state.showPressCtrlToZoomHint}
            onWheel={this.handleOnWheelEvent}
          >
            <div style={{ height: 600 }} id="map" />
          </MapOverlayHintZoom>
        </ProgressOverlay>
      </Paper>
    );
  }

  private addTooltipToPolygonSelections = () => {
    this.state.selectionLayers.map((layer: L.Layer) => {
      const polygonCountsForSelectedLayer = _.find(
        this.props.polygonDensity.polygons,
        {
          // TODO: adequate type for layer.
          id: (layer as any)._leaflet_id,
        }
      );
      if (polygonCountsForSelectedLayer) {
        let tooltipContent = `<table style="width:100%">
        <tr>
          <th style="font-weight:400;text-align:left">Anzahl selektierter Kunden</th>
          <th>${numberFormat(
            (polygonCountsForSelectedLayer as any).count,
            false
          )}</th>
        </tr>
        <tr>
          <th style="font-weight:400;text-align:left">Anzahl Stromverträge</th>
          <th>${numberFormat(
            (polygonCountsForSelectedLayer as any).countElectricity,
            false
          )}</th>
        </tr>
        <tr>
          <th style="font-weight:400;text-align:left">Anzahl Gasverträge</th>
          <th>${numberFormat(
            (polygonCountsForSelectedLayer as any).countGas,
            false
          )}</th>
        </tr>
        <tr>
          <th style="font-weight:400;text-align:left">Anzahl Fernwärmeverträge</th>
          <th>${numberFormat(
            (polygonCountsForSelectedLayer as any).countHeating,
            false
          )}</th>
        </tr>`;

        tooltipContent += isFeatureEnabled(
          process.env.REACT_APP_FEATURE_GLASS_FIBER
        )
          ? `<tr>
                <th style="font-weight:400;text-align:left">Anzahl Glasfaserverträge</th>
                <th>${numberFormat(
                  (polygonCountsForSelectedLayer as any).countGlassFiber,
                  false
                )}</th>
              </tr></table>`
          : "</table>";

        layer.bindTooltip(tooltipContent);
      }
    });
  };

  private reformatLatLngsToPolygonString = (latlngs: L.LatLng[][]): string => {
    const latLngString = latlngs[0]
      .map((latlng: L.LatLng) => `(${latlng.lat}, ${latlng.lng})`)
      .join(",");
    return `(${latLngString})`;
  };

  private getLatLngsFromPolygonString = (polygonString: string): L.LatLng[] => {
    const polygonStringWithoutLeadingAndTrailingBrackets = polygonString.slice(
      1,
      -1
    );
    const latLngStringArrays = polygonStringWithoutLeadingAndTrailingBrackets.split(
      "),("
    );
    const latLngs = latLngStringArrays.map((latLngString) => {
      const latLngArray = latLngString
        .replace("(", "")
        .replace(")", "")
        .split(",");
      return new L.LatLng(
        parseFloat(latLngArray[0]),
        parseFloat(latLngArray[1])
      );
    });
    return latLngs as L.LatLng[];
  };

  private replacePolygonIdInCustomerGroupFilter = (
    oldAndNewPolygonIds: Array<{ old: number; new: number }>
  ) => {
    this.props.changeCustomerGroupFilter({
      polygonMapSelection: this.props.customerGroupFilter.polygonMapSelection.map(
        (selection) => {
          const idArrayEntry = oldAndNewPolygonIds.find(
            (entry) => entry.old === selection.id
          );
          if (idArrayEntry) {
            return { ...selection, id: idArrayEntry.new };
          } else {
            return selection;
          }
        }
      ),
    });
  };

  private drawPolygonsFromCustomerGroupFilter = (
    drawnItems: L.FeatureGroup
  ) => {
    const polygonSelections = this.props.customerGroupFilter
      .polygonMapSelection;
    if (polygonSelections.length) {
      const polygons: L.Polygon[] = [];
      const oldAndNewPolygonIds: Array<{ old: number; new: number }> = [];
      polygonSelections.forEach((selection) => {
        const oldPolygonId = selection.id;
        const latLngs = this.getLatLngsFromPolygonString(selection.polygon);
        const polygon = new L.Polygon(latLngs);
        polygon.addTo(this.map);
        drawnItems.addLayer(polygon);
        polygons.push(polygon);
        const newPolygonId = (polygon as any)._leaflet_id;
        oldAndNewPolygonIds.push({ old: oldPolygonId, new: newPolygonId });
      });
      this.replacePolygonIdInCustomerGroupFilter(oldAndNewPolygonIds);
      this.setState((prev) => ({ ...prev, selectionLayers: polygons }));
    }
  };

  private fitMapBoundsToPolygonSelections = () => {
    /* Retrieving the bounds can not be achieved with Leaflet's getBounds()-method, because
    it interferes with Leaflet.Draw somehow and causes funny behaviour when editing polygons. */
    if (this.state.selectionLayers.length) {
      const lats = _.reduce(
        this.state.selectionLayers,
        (accLayers: number[], layer) =>
          accLayers.concat(
            _.reduce(
              layer.getLatLngs()[0] as L.LatLng[],
              (accLatLngs: number[], latlng) => accLatLngs.concat([latlng.lat]),
              []
            )
          ),
        []
      );

      const lngs = _.reduce(
        this.state.selectionLayers,
        (accLayers: number[], layer) =>
          accLayers.concat(
            _.reduce(
              layer.getLatLngs()[0] as L.LatLng[],
              (accLatLngs: number[], latlng) => accLatLngs.concat([latlng.lng]),
              []
            )
          ),
        []
      );

      const northEastBound = new L.LatLng(_.max(lats)!, _.max(lngs)!);
      const southWestBound = new L.LatLng(_.min(lats)!, _.min(lngs)!);

      const boundsForZoomIn = new L.LatLngBounds(
        southWestBound,
        northEastBound
      );

      const paddingZoomView = new L.Point(100, 100);

      this.map.fitBounds((boundsForZoomIn as unknown) as L.LatLngBounds, {
        padding: paddingZoomView,
      });
    }
  };

  private handleDrawCreated(
    layer: L.Polygon | L.Rectangle,
    map: L.Map,
    drawnItems: any
  ) {
    this.setState((prev) => ({
      ...prev,
      selectionLayers: prev.selectionLayers.concat([layer]),
    }));
    const latlngs: L.LatLng[][] = layer.getLatLngs() as L.LatLng[][];
    drawnItems.addLayer(layer);
    this.props.changeCustomerGroupFilter({
      polygonMapSelection: this.props.customerGroupFilter.polygonMapSelection.concat(
        [
          {
            // TODO: adequate type for layer.
            id: (layer as any)._leaflet_id,
            polygon: this.reformatLatLngsToPolygonString(latlngs),
          },
        ]
      ),
    });
  }

  private handleDrawEdited(layers: L.LayerGroup<L.Polygon | L.Rectangle>) {
    const editedLayers = _.values((layers as any)._layers);
    const newPolygonSelectionValuesFilter = editedLayers.map(
      (layer: L.Polygon | L.Rectangle) => ({
        // TODO: adequate type for layer.
        id: (layer as any)._leaflet_id,
        polygon: this.reformatLatLngsToPolygonString(
          layer.getLatLngs() as L.LatLng[][]
        ),
      })
    );

    this.setState((prev) => ({
      ...prev,
      selectionLayers: prev.selectionLayers.map((selectionLayer: any) => {
        const editedLayer = _.find(editedLayers, {
          _leaflet_id: selectionLayer._leaflet_id,
        });
        return editedLayer ? editedLayer : selectionLayer;
      }),
    }));

    this.props.changeCustomerGroupFilter({
      polygonMapSelection: this.props.customerGroupFilter.polygonMapSelection.map(
        (selection) => {
          const changedSelection = _.find(newPolygonSelectionValuesFilter, {
            id: selection.id,
          });
          return changedSelection !== undefined ? changedSelection : selection;
        }
      ),
    });
  }

  private handleDeleteDraw(layers: L.LayerGroup<L.Polygon | L.Rectangle>) {
    // TODO: adequate type for layers.
    const deletedLayers: number[] = _.values((layers as any)._layers).map(
      (layer) => layer._leaflet_id
    );
    this.setState((prev) => ({
      ...prev,
      selectionLayers: prev.selectionLayers.filter(
        (selectionLayer) =>
          // TODO: adequate type for layers.
          !_.includes(deletedLayers, (selectionLayer as any)._leaflet_id)
      ),
    }));
    this.props.changeCustomerGroupFilter({
      polygonMapSelection: this.props.customerGroupFilter.polygonMapSelection.filter(
        (selection) => !_.includes(deletedLayers, selection.id)
      ),
    });
  }

  private removeClusters(markerClusterGroup: L.MarkerClusterGroup) {
    this.map.removeLayer(markerClusterGroup);
    markerClusterGroup.clearLayers();
  }

  private createClusters(markerClusterGroup: L.MarkerClusterGroup) {
    let marker: L.Marker;
    for (const i of this.props.latLong.data) {
      let tooltipContent =
        `<u><b>Straße</b></u><br>` +
        `<b>${i.customerAddrStreet}` +
        ` ${i.customerAddrHouseNumber}</b>` +
        "<br><br>" +
        `<u><b>Verträge</b></u>` +
        `<br><table style="width:100%" >
        <tr>
          <th style="text-align:left">Strom</th>
          <th>${i.countContractsElectricity}</th>
        </tr>` +
        `<tr>
          <th style="text-align:left">Gas</th>
          <th>${i.countContractsGas}</th>
        </tr>
        <tr>
          <th style="text-align:left">Fernwärme</th>
          <th>${i.countContractsDistrictHeating}</th>
        </tr>
        `;

      tooltipContent += isFeatureEnabled(
        process.env.REACT_APP_FEATURE_GLASS_FIBER
      )
        ? `<tr>
            <th style="text-align:left">Glasfaser</th>
            <th>${i.countContractsGlassFiber}</th>
          </tr>`
        : "";
      marker = L.marker(new L.LatLng(i.latitude, i.longitude), {
        icon: this.singleHouseIcon,
      })
        .bindTooltip(tooltipContent)
        .openTooltip();
      markerClusterGroup.addLayer(marker);
      this.markers.push(marker);
    }
    this.map.addLayer(markerClusterGroup);
  }

  private resetHighlight = (e: any) => {
    this.setState({ density: 0, postcode: "" });
    this.geojson.resetStyle(e.target);
  };

  private onEachFeature = (
    feature: geojson.Feature<geojson.Geometry, any>,
    layer: L.Layer
  ): void => {
    const { count, postcode } = feature.properties;
    if (!this.state.selectionLayers.length) {
      let tooltipContent = `<b><center>${postcode}</center></b><br> <table style="width:100%">
      <tr>
        <th style="font-weight:400;text-align:left">Anzahl selektierter Kunden</th>
        <th>${numberFormat(count.count, false)}</th>
      </tr>
      <tr>
        <th style="font-weight:400;text-align:left">Anzahl Stromverträge</th>
        <th>${numberFormat(count.count_electricity, false)}</th>
      </tr>
      <tr>
        <th style="font-weight:400;text-align:left">Anzahl Gasverträge</th>
        <th>${numberFormat(count.count_gas, false)}</th>
      </tr>
      <tr>
        <th style="font-weight:400;text-align:left">Anzahl Fernwärmeverträge</th>
        <th>${numberFormat(count.count_heating, false)}</th>
      </tr>`;

      tooltipContent += isFeatureEnabled(
        process.env.REACT_APP_FEATURE_GLASS_FIBER
      )
        ? `<tr>
              <th style="font-weight:400;text-align:left">Anzahl Glasfaserverträge</th>
              <th>${numberFormat(count.count_glass_fiber, false)}</th>
            </tr></table>`
        : "</table>";
      layer.bindTooltip(tooltipContent);

      layer.on({
        click: this.handleClick,
        mouseout: this.resetHighlight,
        mouseover: this.highlightFeature,
      });
    }
  };

  private createMapLegend() {
    this.legend.onAdd = () => {
      const { classes } = this.props;
      const div = L.DomUtil.create("div", `${classes && classes.legend}`);
      const title = "Anzahl selektierter<br />Kunden je PLZ";
      const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

      const maxCount = _.max(
        this.props.customerDensity.features.map(
          (feature) => feature.properties.count.count
        )
      );

      div.innerHTML += `<h4>${title}</h4>`;
      for (const grade of grades.reverse()) {
        div.innerHTML +=
          `<i><img style="background:${this.getColor(
            1,
            grade
            /* compared to the feature color inside the map the opacity is higher here which is intentional,
            since the map background is darker / more saturated than the white legend background. The aim here
            is a best possible match between the feature colors and the legend colors. */
          )};opacity:0.8;width:15px;height:15px;margin-right:10"></img>` +
          "&nbsp" +
          Math.floor(((grade - 1) / 10) * maxCount) +
          "&nbsp" +
          (grade
            ? " &ndash; " + Math.floor((grade / 10) * maxCount) + `</i><br />`
            : "+");
      }
      return div;
    };

    this.legend.addTo(this.map);
  }

  private removeMapLegend() {
    this.legend.remove();
  }
  private handleOnWheelEvent = () => {
    this.enableMapOverlayHintZoom();
    this.disableMapOverlayHintZoomAfterTimeout();
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.ctrlKey) {
      this.map.scrollWheelZoom.enable();
      this.setState({ showPressCtrlToZoomHint: false });
      this.ctrlKeyDown = true;
    }
  };

  private handleKeyUp = () => {
    this.map.scrollWheelZoom.disable();
    this.ctrlKeyDown = false;
  };

  private handleClick = (event: L.LeafletEvent) => {
    const layer = event.target;
    const { postcode } = layer.feature.properties;
    const {
      customerAddressPostalCodes,
      filterGroupsEnabled,
    } = this.props.customerGroupFilter;

    if (
      _.includes(
        customerAddressPostalCodes.map((item) => item.value),
        postcode
      )
    ) {
      this.props.changeCustomerGroupFilter({
        customerAddressPostalCodes: _.reject(
          customerAddressPostalCodes,
          (item) => item.value === postcode
        ),
        filterGroupsEnabled: {
          ...filterGroupsEnabled,
          customerInformation: true,
        },
      });
    } else {
      this.props.changeCustomerGroupFilter({
        customerAddressPostalCodes: [
          ...customerAddressPostalCodes,
          { label: postcode, value: postcode },
        ],
        filterGroupsEnabled: {
          ...filterGroupsEnabled,
          customerInformation: true,
        },
      });
    }
  };

  private highlightFeature = (e: L.LeafletEvent): void => {
    const layer = e.target;
    const { density, postcode } = layer.feature.properties;
    this.setState({
      density,
      postcode,
    });

    layer.setStyle({
      color: _.includes(
        this.props.customerGroupFilter.customerAddressPostalCodes.map(
          (item) => item.value
        ),
        postcode
      )
        ? this.props.theme.palette.secondary.main
        : this.props.theme.palette.primary.main,
      dashArray: "",
    });

    if (!L.Browser.ie && !L.Browser.opera12 && !L.Browser.edge) {
      layer.bringToFront();
    }
  };

  private getColor = (count?: number, density?: number) => {
    const color = indigo;

    if (!count || density === undefined) {
      return "transparent";
    }

    return density > 9
      ? color[900]
      : density > 8
      ? color[800]
      : density > 7
      ? color[700]
      : density > 6
      ? color[600]
      : density > 5
      ? color[500]
      : density > 4
      ? color[400]
      : density > 3
      ? color[300]
      : density > 2
      ? color[200]
      : density > 1
      ? color[100]
      : color[50];
  };

  private fetchData = async () => {
    this.props.fetchCustomerDensity(this.props.customerGroupFilter);
    this.props.fetchLatLong(this.props.customerGroupFilter);
  };
}
const styles: StyleRulesCallback<Theme, Props> = (theme: Theme) => ({
  legend: {
    background: "white",
    border: "2px solid black",
    borderRadius: "15px",
    opacity: 1,
    paddingBottom: 10,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 0,
    textAlign: "left",
    width: "auto",
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(withTheme(MapSelection)));



// WEBPACK FOOTER //
// ./src/components/MapSelection.tsx